import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { Buildings, MapPin, MagnifyingGlass, PlusCircle, GraduationCap, ArrowRight } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageIntro } from "@/components/PageIntro";
import SampleLinks from "@/components/SampleLinks";
import { NotFindingInstitution } from "@/components/NotFindingInstitution";
import InstitutionLocationFinder from "@/components/InstitutionLocationFinder";
import AlmaMaterPromo from "@/components/AlmaMaterPromo";
import SaveInstitutionButton from "@/components/SaveInstitutionButton";
import { isIndia } from "@/lib/edition";

export default function PublicInstitutions() {
  useSeo("Education Institutions", "Search the global directory of schools, colleges and universities.");
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const goToCountry = (v) => {
    if (v.country) navigate(`/countries/${encodeURIComponent(v.country)}${v.state ? `?state=${encodeURIComponent(v.state)}` : ""}`);
  };
  const [q, setQ] = useState(params.get("q") || "");
  const [loc, setLoc] = useState({ country: params.get("country") || (isIndia ? "India" : ""), state: "", city: "" });
  const [items, setItems] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());

  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    api.get("/profiles/me").then((r) => setSavedIds(new Set((r.data.saved_institutions || []).map((s) => s.institution_id)))).catch(() => {});
  }, [user]);

  const load = useCallback(() => {
    setItems(null);
    api.get("/institutions", { params: { q, country: loc.country, state: loc.state, city: loc.city, limit: 60 } })
      .then((r) => setItems(r.data.results || []))
      .catch(() => setItems([]));
  }, [q, loc]);
  useEffect(() => { load(); }, [load]);

  const [news, setNews] = useState([]);
  useEffect(() => {
    if (!loc.country) { setNews([]); return; }
    api.get("/news", { params: { country: loc.country } }).then((r) => setNews((r.data.items || []).slice(0, 5))).catch(() => setNews([]));
  }, [loc.country]);

  const [browse, setBrowse] = useState([]);
  useEffect(() => { api.get("/institutions/locations").then((r) => setBrowse(r.data || [])).catch(() => setBrowse([])); }, []);
  const FOCUS = ["India", "United States", "United Kingdom", "Australia", "Canada", "Sri Lanka", "Pakistan"];
  const browseSorted = [...browse].sort((a, b) => {
    const fa = FOCUS.indexOf(a.country), fb = FOCUS.indexOf(b.country);
    if (fa !== -1 || fb !== -1) return (fa === -1 ? 99 : fa) - (fb === -1 ? 99 : fb);
    return (b.count || 0) - (a.count || 0);
  });
  const activeCountry = browseSorted.find((c) => c.country === loc.country);

  const onLoc = (v) => {
    setLoc(v);
    const p = new URLSearchParams(params);
    if (v.country) p.set("country", v.country); else p.delete("country");
    setParams(p);
  };
  const toggleSaved = (id, isSaved) => setSavedIds((prev) => { const n = new Set(prev); isSaved ? n.add(id) : n.delete(id); return n; });

  return (
    <div className="mx-auto max-w-7xl px-5 py-14">
      <SampleLinks className="mb-6" />
      <AlmaMaterPromo className="mb-6" />
      <Link to="/kerala-schools" data-testid="kerala-directory-banner"
        className="mb-6 flex items-center justify-between gap-3 rounded-2xl border border-green-200 bg-gradient-to-r from-green-50 to-orange-50 px-5 py-3.5 transition-colors hover:border-green-400">
        <span className="flex items-center gap-2 text-sm">
          <GraduationCap size={20} weight="duotone" className="text-green-600" />
          <span className="font-medium text-foreground">Kerala schools</span>
          <span className="text-muted-foreground">— browse all schools by district with direct alumni links</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-primary">Explore <ArrowRight size={15} /></span>
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-2xl">
          <p className="overline text-secondary mb-3">Directory</p>
          <h1 className="font-heading text-4xl font-light tracking-tight">Education institutions</h1>
          <p className="mt-3 text-muted-foreground">Find schools, colleges and universities — and their alumni communities. Missing yours? Suggest it and we'll add it {isIndia ? "to the directory" : "worldwide"}.</p>
        </div>
        <Link to="/institutions/suggest">
          <Button className="rounded-full gap-2" data-testid="suggest-inst-open"><PlusCircle size={16} weight="fill" /> Suggest an Institution</Button>
        </Link>
      </div>

      <PageIntro
        title={isIndia ? "India's directory of education" : "A global directory of education"}
        tagline="Discover the places that shaped lives — and the alumni connected to them."
        intro={isIndia ? [
          "From village schools to the IITs, we're building the most complete directory of Indian educational institutions — so people can find the schools, colleges and universities that shaped their lives and reconnect with their alumni communities.",
          "Can't find your institution? Suggest it, and help us map every corner of India, one institution at a time.",
        ] : [
          "We're building a worldwide directory of educational institutions so people can find the schools, colleges, universities, campuses, and departments that shaped their lives — and discover the alumni communities connected to them.",
          "Can't find your institution? Suggest it, and help us build a more complete picture of education around the world, one institution at a time.",
        ]}
      />


      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name…" className="rounded-full pl-10" data-testid="inst-search" />
        </div>
      </div>
      <div className="mt-3 rounded-2xl border border-slate-100 border-t-4 border-t-green-600 bg-white p-4 shadow-sm">
        <InstitutionLocationFinder value={loc} onChange={onLoc} onGo={goToCountry} />
      </div>

      {/* Browse by country / state — hidden in the India edition (India-only) */}
      {!isIndia && browseSorted.length > 0 && (
        <div className="mt-6" data-testid="browse-by-country">
          <p className="mb-2 text-sm font-medium text-muted-foreground">Browse by country</p>
          <div className="flex flex-wrap gap-2">
            {browseSorted.map((c) => (
              <button key={c.country} data-testid={`browse-country-${c.country.replace(/\s+/g, "-").toLowerCase()}`}
                onClick={() => onLoc({ country: c.country === loc.country ? "" : c.country, state: "", city: "" })}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm transition-colors ${c.country === loc.country ? "border-green-600 bg-green-600 text-white" : "border-slate-200 bg-white text-foreground hover:border-green-400 hover:bg-green-50"}`}>
                {c.country}
                <span className={`rounded-full px-1.5 py-0.5 text-[11px] ${c.country === loc.country ? "bg-white/25" : "bg-slate-100 text-muted-foreground"}`}>{c.count}</span>
              </button>
            ))}
          </div>
          {activeCountry && activeCountry.states.filter((s) => s.state !== "—").length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="browse-by-state">
              <span className="self-center text-sm text-muted-foreground">{loc.country} states/regions:</span>
              {activeCountry.states.filter((s) => s.state !== "—").map((s) => (
                <button key={s.state} data-testid={`browse-state-${s.state.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => onLoc({ country: loc.country, state: s.state === loc.state ? "" : s.state, city: "" })}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${s.state === loc.state ? "border-orange-500 bg-orange-500 text-white" : "border-slate-200 bg-white text-foreground hover:border-orange-400 hover:bg-orange-50"}`}>
                  {s.state}<span className={`rounded-full px-1.5 py-0.5 text-[10px] ${s.state === loc.state ? "bg-white/25" : "bg-slate-100 text-muted-foreground"}`}>{s.count}</span>
                </button>
              ))}
            </div>
          )}
          {activeCountry && loc.state && (() => {
            const st = activeCountry.states.find((s) => s.state === loc.state);
            const cities = st ? st.cities : [];
            return cities.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="browse-by-city">
                <span className="self-center text-sm text-muted-foreground">{loc.state} districts/cities:</span>
                {cities.map((ci) => (
                  <button key={ci} data-testid={`browse-city-${ci.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => onLoc({ country: loc.country, state: loc.state, city: ci === loc.city ? "" : ci })}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${ci === loc.city ? "border-primary bg-primary text-primary-foreground" : "border-slate-200 bg-white text-foreground hover:border-primary/40 hover:bg-primary/5"}`}>
                    {ci}
                  </button>
                ))}
              </div>
            ) : null;
          })()}
        </div>
      )}

      {loc.country && news.length > 0 && (
        <div className="mt-8 rounded-2xl border border-border/60 bg-muted/30 p-6" data-testid="local-news">
          <h2 className="font-heading text-lg font-medium">Education news in {loc.country}</h2>
          <div className="mt-3 divide-y divide-border/50">
            {news.map((n) => (
              <a key={n.id} href={n.url} target="_blank" rel="noreferrer" className="flex items-center justify-between gap-3 py-2.5 text-sm hover:text-secondary">
                <span className="min-w-0"><span className="mr-2 text-xs font-medium text-secondary">{n.source}</span>{n.title}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {items === null ? <p className="mt-10 text-muted-foreground">Loading…</p>
        : items.length === 0 ? <p className="mt-10 text-muted-foreground">No institutions found.</p>
        : (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((i) => (
              <div key={i.id} className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md" data-testid={`inst-card-${i.id}`}>
                <Link to={`/institutions/${i.id}`}>
                  <div className="flex h-32 items-center justify-center bg-gradient-to-br from-green-50 via-white to-orange-50">
                    {i.logo_url
                      ? <img src={i.logo_url} alt={i.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                      : <Buildings size={40} weight="duotone" className="text-green-600/70" />}
                  </div>
                  <div className="p-5 pb-14">
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-full border-green-200 bg-green-50 font-normal text-green-800" variant="outline">{i.type}</Badge>
                    </div>
                    <h3 className="mt-2 font-heading text-lg font-medium leading-tight">{i.name}</h3>
                    {(i.location || i.country) && <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin size={14} className="text-orange-500" /> {[i.location, i.country].filter(Boolean).join(", ")}</p>}
                  </div>
                </Link>
                <div className="absolute bottom-4 left-5">
                  <SaveInstitutionButton instId={i.id} saved={savedIds.has(i.id)} onToggle={toggleSaved} />
                </div>
              </div>
            ))}
          </div>
        )}

      <NotFindingInstitution className="mt-14" />
    </div>
  );
}
